import React, { useRef, useState } from 'react';
import { AvButton, FilterStyledPopper, Select } from '@fabric-ui/components';
import { Settings } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, ClickAwayListener, Divider, SvgIcon, svgIconClasses } from '@mui/material';
import { PendoFeature } from '@utils/pendo/PendoFeature';

export interface OptionsPopupProps {
  fieldName: string;
  onApply: (value: any) => void;
  initialValue: string;
  label: string;
  options: { value: string; title: string }[];
  shouldDisplayIcon: boolean;
}

function OptionsPopup({ fieldName, onApply, initialValue, label, options, shouldDisplayIcon }: OptionsPopupProps) {
  const buttonRef = useRef(null);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [selectedValue, setSelectedValue] = useState(initialValue);

  const onClick = event => {
    event.stopPropagation();
    setIsPopupOpen(true);
  };

  const closePopup = event => {
    event.stopPropagation();
    setIsPopupOpen(false);
  };

  const onApplyClick = event => {
    onApply(selectedValue);
    closePopup(event);
  };

  const onChange = value => {
    setSelectedValue(value);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        [`& button .${svgIconClasses.root}`]: {
          ...((shouldDisplayIcon || isPopupOpen) && { opacity: 1 }),
        },
      }}
      onClick={event => event.stopPropagation()}>
      <AvButton title={`${fieldName} Settings`} variant="link" active={isPopupOpen} onClick={onClick} ref={buttonRef}>
        <SvgIcon sx={iconSize(16)}>
          <Settings />
        </SvgIcon>
      </AvButton>

      <ClickAwayListener onClickAway={closePopup} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <FilterStyledPopper open={isPopupOpen} anchorEl={buttonRef.current} placement="top-start" sx={{ padding: 0, position: 'relative' }}>
          <Box sx={{ padding: 3 }}>
            <Select
              value={selectedValue}
              options={options}
              onChange={onChange}
              label={label}
              size="small"
              style={{ width: '240px' }}
              muiProps={{ disableClearable: true }}
            />
          </Box>

          <Divider />

          <Box sx={{ ...flex.justifyEnd, gap: 1, padding: '8px 16px' }}>
            <Button size="small" onClick={closePopup}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={onApplyClick} data-pendo-id={PendoFeature.TimeBucketingApply}>
              Apply
            </Button>
          </Box>
        </FilterStyledPopper>
      </ClickAwayListener>
    </Box>
  );
}

export default OptionsPopup;
